import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  Paper,
  Grid,
  Typography,
  makeStyles,
  Tooltip,
  Chip
} from '@material-ui/core'
import {
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  DragIndicator as DragIndicatorIcon,
  Functions as CalcIcon
} from '@material-ui/icons'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import axios from 'axios'
import { currencyFormat } from '../../services/common'
import { toNestedShape } from '../../utils/rateShapeAdapter'
import { Autocomplete } from '@material-ui/lab'

const unitsOfMeasure = [
  'Each', 'Flat', 'Pint', 'Quart',
  '1 Gallon', '2 Gallon', '3 Gallon', '5 Gallon', '10 Gallon', '15 Gallon', '25 Gallon',
  'Bare Root',
  '24" Box', '30" Box', '32" Box', '36" Box', '42" Box', '48" Box',
  '54" Box', '60" Box', '66" Box', '70" Box', '72" Box', '78" Box', '84" Box',
  'Square Foot', 'Linear Foot', 'Ton'
]

const lineItemTypes = ['Labor', 'Materials', 'Equipment', 'SubContractor', 'Notes', 'Measurements']

const formatLabel = (camelCase) => {
  return camelCase
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str?.toUpperCase())
}

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: '90vw',
    maxWidth: '90vw',
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
    maxHeight: '38vh',
    overflow: 'auto',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
    },
  },
  selectedRow: {
    backgroundColor: '#e3f2fd !important',
    borderLeft: '3px solid ' + theme.palette.primary.main,
  },
  formSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  formTitle: {
    marginBottom: theme.spacing(2),
  },
  fieldSet: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fieldSetTitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  total: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  totalText: {
    marginRight: theme.spacing(2),
  },
  typeChip: {
    fontWeight: 600,
    fontSize: 11,
  },
  dragHandleCell: {
    padding: '4px 2px',
    width: 40,
    cursor: 'grab',
    color: theme.palette.text.disabled,
  },
  actionsCell: {
    padding: '2px 8px',
    width: 80,
  },
  autoDesc: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: 4,
  },
  calcToolbar: {
    display: 'flex',
    gap: 8,
    padding: '8px 0',
    marginBottom: 8,
  },
  calcButton: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
  },
  calcPanel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    backgroundColor: '#fafafa',
  },
  calcResultRow: {
    backgroundColor: '#e8f5e9',
  },
}))

const TYPE_COLORS = {
  Labor: '#1976d2',
  Materials: '#388e3c',
  Equipment: '#f57c00',
  SubContractor: '#00838f',
  Notes: '#9e9e9e',
  Measurements: '#7b1fa2',
}

// Equipment → Operator pairing. When equipment is selected, an operator line item is auto-added.
const EQUIPMENT_OPERATOR_MAP = {
  bobcat: { operatorKey: 'bobcatOperator', label: 'Bobcat Operator' },
  crane: { operatorKey: 'craneOperator', label: 'Crane Operator' },
  tractor: { operatorKey: 'tractorOperator', label: 'Tractor Operator' }
}

// Only show actual equipment in the dropdown (not operators, not _id)
const EQUIPMENT_ONLY_KEYS = new Set(['bobcat', 'crane', 'tractor', 'truckDriver'])

const EMPTY_ITEM = {
  qty: '',
  type: '',
  description: '',
  displayDescription: '',
  displayQty: true,
  displaySize: true,
  costPer: '',
  pricePer: '',
  taxable: true,
  laborType: '',
  equipmentType: '',
  unit: 'Each',
  materialId: null,
  optionId: null,
  size: '',
  crewSize: 1,
  totalHours: 0,
}

const LineItemBuilder = ({
  open,
  onClose,
  onSubmit,
  initialLineItems = [],
  client,
  entities = [],
  url,
  takeOffList
}) => {
  const classes = useStyles()
  const [lineItems, setLineItems] = useState(initialLineItems)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [editMode, setEditMode] = useState('new')
  const [editingItem, setEditingItem] = useState({ ...EMPTY_ITEM })
  const [selectedMaterial, setSelectedMaterial] = useState(null)
  const [materialOptions, setMaterialOptions] = useState([])
  const [showMaterialCalc, setShowMaterialCalc] = useState(false)
  const [materialCalcInputs, setMaterialCalcInputs] = useState({ squareFeet: 0, depth: 2, installMethod: 'hand', difficulty: 'standard', addLabor: true, addEquipment: true })
  const [materialCalcCompanions, setMaterialCalcCompanions] = useState([])

  // Calculator state
  const [activeCalc, setActiveCalc] = useState(null) // 'plants'|'gravel'|'irrigation'|'lighting'|null
  const [calcConfig, setCalcConfig] = useState(null)
  const [calcLoading, setCalcLoading] = useState(false)
  const [gravelInputs, setGravelInputs] = useState({ type: 'halfInch', squareFeet: 1000, depth: 2, installMethod: 'hand', difficulty: 'standard' })
  const [plantRows, setPlantRows] = useState([{ size: 'mediumPlants', quantity: 0, difficulty: 'standard', soilType: 'loamy' }])
  const [irrigationInputs, setIrrigationInputs] = useState({ plantCount: 0, treeCount: 0, includeBackflow: true, includeClock: true })
  const [lightingInputs, setLightingInputs] = useState({ lightCount: 20, wattsPerLight: 10 })

  const [resolvedLaborRates, setResolvedLaborRates] = useState(null)
  const [resolvedEquipmentRates, setResolvedEquipmentRates] = useState(null)

  useEffect(() => {
    const clientId = client?._id
    if (!url) return
    const qs = clientId ? `?clientId=${clientId}` : ''
    axios.get(`${url}/api/bid-estimation/rates/resolved${qs}`)
      .then(res => {
        setResolvedLaborRates(toNestedShape(res.data.laborRates || []))
        setResolvedEquipmentRates(toNestedShape(res.data.equipmentRates || []))
      })
      .catch(console.error)
  }, [client?._id, url])

  useEffect(() => {
    setLineItems(initialLineItems)
    setSelectedIndex(-1)
    setEditMode('new')
    setEditingItem({ ...EMPTY_ITEM })
  }, [initialLineItems])

  // Load calc config when a calculator is opened
  useEffect(() => {
    if (activeCalc && !calcConfig) {
      setCalcLoading(true)
      Promise.all([
        axios.get('/api/calculation-configs'),
        axios.get('/api/calculation-configs/user/preference')
      ]).then(([configsRes, prefRes]) => {
        const allConfigs = configsRes.data.configs || []
        const prefId = prefRes.data.configId
        const preferred = prefId ? allConfigs.find(c => c._id === prefId) : null
        setCalcConfig(preferred || allConfigs[0] || null)
      }).catch(() => {}).finally(() => setCalcLoading(false))
    }
  }, [activeCalc])

  // Auto-count plants from existing sub-line items for the irrigation calculator
  useEffect(() => {
    if (activeCalc === 'irrigation') {
      let plantCount = 0
      let treeCount = 0
      lineItems.forEach(item => {
        if (item.type !== 'Materials') return
        const desc = (item.description || '').toLowerCase()
        const qty = parseFloat(item.qty) || 0
        const size = (item.size || item.unit || '').toLowerCase()
        if (size.includes('box') || size.includes('24') || size.includes('36') || desc.includes('tree')) {
          treeCount += qty
        } else if (qty > 0) {
          plantCount += qty
        }
      })
      setIrrigationInputs(prev => ({ ...prev, plantCount, treeCount }))
    }
  }, [activeCalc, lineItems])

  const theEntity = entities.find(ent => ent._id === client?.entity)
  // Prefer unified resolved rates; fall back to embedded fields while API call is in flight
  const laborRates = resolvedLaborRates || client?.laborRates || theEntity?.laborRates
  const equipmentRates = resolvedEquipmentRates || client?.equipmentRates || theEntity?.equipmentRates

  // ── Rate helpers ──────────────────────────────────────────
  const getRate = (type, rateType) => {
    const rateMap = type === 'Labor' ? laborRates : equipmentRates
    return rateMap?.[rateType]?.rate ?? 0
  }

  const getCrewSize = (type, rateType) => {
    const rateMap = type === 'Labor' ? laborRates : equipmentRates
    return rateMap?.[rateType]?.defaultCrewSize ?? 1
  }

  // ── Auto-generate description ─────────────────────────────
  const buildAutoDescription = (item) => {
    if (item.type === 'Labor' && item.laborType) {
      return `${formatLabel(item.laborType)} Labor`
    }
    if (item.type === 'Equipment' && item.equipmentType) {
      return `${formatLabel(item.equipmentType)}`
    }
    if (item.type === 'Materials' && selectedMaterial) {
      const optName = item.optionId
        ? (materialOptions.find(o => o._id === item.optionId)?.name ||
           materialOptions.find(o => o._id === item.optionId)?.size || '')
        : ''
      return optName
        ? `${selectedMaterial.name} - ${optName}`
        : selectedMaterial.name
    }
    return ''
  }

  // ── Calculations ──────────────────────────────────────────
  const calculateTotal = (item) => {
    if (item.type === 'Notes' || item.type === 'Measurements') return 0
    if (item.type === 'Equipment') {
      // Equipment is priced per day: days × pricePerDay
      return (parseFloat(item.qty) || 0) * (parseFloat(item.pricePer) || 0)
    }
    if (item.type === 'Labor') {
      // Labor is priced per hour: days × crewSize × 8hrs × pricePerHour
      const totalHours = (parseFloat(item.qty) || 0) * (item.crewSize || 1) * 8
      return totalHours * (parseFloat(item.pricePer) || 0)
    }
    return (parseFloat(item.qty) || 0) * (parseFloat(item.pricePer) || 0)
  }

  const calculateGrandTotal = () => {
    return lineItems.reduce((sum, item) => sum + calculateTotal(item), 0)
  }

  const isItemValid = (item) => {
    if (item.type === 'Notes' || item.type === 'Measurements') {
      return item.type && item.description
    }
    return (
      item.qty &&
      item.type &&
      (item.description || buildAutoDescription(item)) &&
      item.pricePer &&
      ((item.type === 'Labor' && item.laborType) ||
       (item.type === 'Equipment' && item.equipmentType) ||
       (item.type !== 'Labor' && item.type !== 'Equipment'))
    )
  }

  const determineUnit = (item) => item?.size ? item.size : item?.unit || 'Each'

  // ── Handlers ──────────────────────────────────────────────
  const handleTypeChange = (e) => {
    const newType = e.target.value
    const defaults = { ...EMPTY_ITEM, type: newType, description: '' }
    if (newType === 'SubContractor') {
      defaults.pricePer = 1.15
      defaults.costPer = 1
      defaults._markupPct = 15
      defaults.displayQty = false
      defaults.unit = 'Each'
    }
    setEditingItem(defaults)
    setSelectedMaterial(null)
    setMaterialOptions([])
  }

  const DEFAULT_LABOR_BURDEN_RATE = 33

  const handleRateTypeChange = (e) => {
    const rateType = e.target.value
    const type = editingItem.type
    const chargeRate = getRate(type, rateType)
    const crewSize = getCrewSize(type, rateType)

    setEditingItem(prev => ({
      ...prev,
      [type === 'Labor' ? 'laborType' : 'equipmentType']: rateType,
      // Labor: rate from entity/client is what we CHARGE, cost defaults to burden rate
      // Equipment: rate is per day, cost = rate, price = rate × markup
      costPer: type === 'Labor' ? DEFAULT_LABOR_BURDEN_RATE : chargeRate,
      pricePer: type === 'Labor' ? chargeRate : +(chargeRate * 1.4).toFixed(2),
      crewSize,
      totalHours: crewSize * (parseFloat(prev.qty) || 0) * 8,
      description: '',
    }))
  }

  const handleQuantityChange = (e) => {
    const days = parseFloat(e.target.value) || 0
    const crewSize = editingItem.crewSize || 1
    setEditingItem(prev => ({
      ...prev,
      qty: e.target.value,
      totalHours: days * crewSize * 8
    }))
  }

  const handleMaterialChange = (event, newValue) => {
    setSelectedMaterial(newValue)
    if (newValue && newValue.options) {
      setMaterialOptions(newValue.options)
      setEditingItem(prev => ({
        ...prev,
        materialId: newValue._id,
        description: '', // auto-desc will populate
      }))
    } else {
      setMaterialOptions([])
      setEditingItem(prev => ({
        ...prev,
        materialId: null,
        optionId: null,
        description: '',
      }))
    }
  }

  const handleOptionChange = (e) => {
    const selectedOption = materialOptions.find(opt => opt._id === e.target.value)
    if (selectedOption) {
      const costPer = parseFloat(selectedOption.price) || 0
      const pricePer = parseFloat(selectedOption.msrp) || (costPer * 1.4)
      setEditingItem(prev => ({
        ...prev,
        optionId: selectedOption._id,
        size: selectedOption.size,
        costPer,
        pricePer,
        description: '', // auto-desc handles it
      }))
    }
  }

  const handleEditItem = (index) => {
    const item = lineItems[index]
    if (item?.materialId && takeOffList) {
      const mats = takeOffList.find(to => to._id === item.materialId)
      if (mats?._id) {
        setSelectedMaterial(mats)
        setMaterialOptions(mats.options || [])
      }
    }
    setSelectedIndex(index)
    setEditingItem({ ...item })
    setEditMode('edit')
  }

  // Unique ID for drag-and-drop (use _id from DB or generate one)
  const getDragId = (item, index) => item._id || item._dragId || `item-${index}`

  const handleSave = () => {
    const finalItem = { ...editingItem }
    if (!finalItem.description) {
      finalItem.description = buildAutoDescription(finalItem)
    }
    if (!finalItem._id && !finalItem._dragId) {
      finalItem._dragId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    }

    // Build the operator line item if this is equipment with a paired operator
    let operatorItem = null
    if (editMode === 'new' && finalItem.type === 'Equipment' && finalItem.equipmentType) {
      const mapping = EQUIPMENT_OPERATOR_MAP[finalItem.equipmentType]
      if (mapping) {
        const opRate = getRate('Labor', mapping.operatorKey)
        const opCrewSize = getCrewSize('Labor', mapping.operatorKey)
        const days = parseFloat(finalItem.qty) || 0
        operatorItem = {
          ...EMPTY_ITEM,
          type: 'Labor',
          laborType: 'operator',
          description: mapping.label,
          qty: finalItem.qty,
          costPer: DEFAULT_LABOR_BURDEN_RATE,
          pricePer: opRate,
          crewSize: opCrewSize,
          totalHours: days * opCrewSize * 8,
          _dragId: `op-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
        }
      }
    }

    if (editMode === 'new') {
      setLineItems(prev => {
        const newItems = [...prev, finalItem]
        if (operatorItem) newItems.push(operatorItem)
        if (materialCalcCompanions.length > 0) newItems.push(...materialCalcCompanions)
        return newItems
      })
    } else {
      setLineItems(prev => {
        const updated = prev.map((item, i) => i === selectedIndex ? finalItem : item)
        // Add calculator companion items when editing (they were queued, not yet in the list)
        if (materialCalcCompanions.length > 0) updated.push(...materialCalcCompanions)
        return updated
      })
    }
    setSelectedIndex(-1)
    setEditingItem({ ...EMPTY_ITEM })
    setEditMode('new')
    setSelectedMaterial(null)
    setMaterialOptions([])
    setMaterialCalcCompanions([])
    setShowMaterialCalc(false)
  }

  const handleDelete = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index))
    if (selectedIndex === index) {
      setSelectedIndex(-1)
      setEditingItem({ ...EMPTY_ITEM })
      setEditMode('new')
    } else if (selectedIndex > index) {
      setSelectedIndex(selectedIndex - 1)
    }
  }

  const handleDuplicate = (index) => {
    const copy = { ...lineItems[index], _id: undefined, _dragId: `dup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }
    const newItems = [...lineItems]
    newItems.splice(index + 1, 0, copy)
    setLineItems(newItems)
  }

  const handleDragEnd = (result) => {
    if (!result.destination) return
    const srcIdx = result.source.index
    const destIdx = result.destination.index
    if (srcIdx === destIdx) return

    const newItems = Array.from(lineItems)
    const [moved] = newItems.splice(srcIdx, 1)
    newItems.splice(destIdx, 0, moved)
    setLineItems(newItems)

    // Track selected item through the move
    if (selectedIndex === srcIdx) {
      setSelectedIndex(destIdx)
    } else if (selectedIndex > srcIdx && selectedIndex <= destIdx) {
      setSelectedIndex(selectedIndex - 1)
    } else if (selectedIndex < srcIdx && selectedIndex >= destIdx) {
      setSelectedIndex(selectedIndex + 1)
    }
  }

  const handleCancelEdit = () => {
    setSelectedIndex(-1)
    setEditingItem({ ...EMPTY_ITEM })
    setEditMode('new')
    setSelectedMaterial(null)
    setMaterialOptions([])
  }

  const handleSaveLineItems = () => {
    const newItems = lineItems.map(item => ({
      ...item,
      totalPrice: calculateTotal(item),
      lineItemType: item.type,
      unit: determineUnit(item)
    }))
    onSubmit(newItems)
  }

  // ── Material type detection ────────────────────────────────
  const getMaterialCalcType = () => {
    // Always available — user can open it for any material
    return 'tonnage'
  }

  // ── Tonnage calculator (for gravel/rock/boulders) ─────────
  const calcTonnage = () => {
    if (!calcConfig || !materialCalcInputs.squareFeet) return { tons: 0, laborDays: 0, laborCost: 0, equipCost: 0 }
    const coverage = getCalcVal('gravelCoverage.halfInch') || 100 // default coverage
    const depthMult = materialCalcInputs.depth / 2
    const tonsRaw = (materialCalcInputs.squareFeet / (coverage || 1)) * depthMult
    const tons = Math.ceil(tonsRaw * 1.10) // +10% fudge factor, rounded up to whole tons

    const installRate = materialCalcInputs.installMethod === 'hand'
      ? getCalcVal('materialRates.gravelTonsPerDay')
      : materialCalcInputs.installMethod === 'tractor'
        ? getCalcVal('moveGravelRates.gravelTonsPerDayTractor')
        : getCalcVal('moveGravelRates.gravelTonsPerDayBobcat')
    const diffFactor = getCalcVal(`difficultyFactors.${materialCalcInputs.difficulty}`) || 1
    const adjRate = (installRate || 1) / diffFactor
    const laborDaysRaw = tons / (adjRate || 1)
    const laborDays = Math.max(0.5, Math.ceil(laborDaysRaw * 2) / 2) // round up to nearest 0.5, minimum 0.5
    const dayRate = getCalcVal('labor.dayRate')
    const equipRate = materialCalcInputs.installMethod === 'tractor'
      ? getCalcVal('equipment.tractorRentalDailyRate')
      : materialCalcInputs.installMethod === 'bobcat'
        ? getCalcVal('equipment.bobcatRentalDailyRate')
        : 0

    return {
      tons,
      laborDays,
      laborCost: +(laborDays * dayRate).toFixed(2),
      equipCost: +(laborDays * equipRate).toFixed(2),
      dayRate,
      equipRate
    }
  }

  const handleApplyTonnageCalc = () => {
    const r = calcTonnage()

    // Use entity/client labor rates — not the calc config day rate
    const laborRate = getRate('Labor', 'labor') || 60
    const defaultCrewSize = getCrewSize('Labor', 'labor') || 3
    const operatorRate = getRate('Labor', 'operator') || 90
    const operatorCrewSize = getCrewSize('Labor', 'operator') || 1

    // Build companion items (labor + equipment + operator)
    const companions = []
    if (r.laborDays > 0) {
      companions.push({
        ...EMPTY_ITEM,
        type: 'Labor', laborType: 'labor',
        description: 'Install Labor',
        qty: r.laborDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: laborRate,
        crewSize: defaultCrewSize, totalHours: r.laborDays * defaultCrewSize * 8,
        _dragId: makeId()
      })
    }
    if (materialCalcInputs.installMethod !== 'hand' && r.equipCost > 0) {
      const equipType = materialCalcInputs.installMethod === 'bobcat' ? 'bobcat' : 'tractor'
      companions.push({
        ...EMPTY_ITEM,
        type: 'Equipment', equipmentType: equipType,
        description: formatLabel(equipType),
        qty: r.laborDays, costPer: r.equipRate, pricePer: +(r.equipRate * 1.4).toFixed(2),
        crewSize: 1, totalHours: r.laborDays * 8,
        _dragId: makeId()
      })
      const opKey = EQUIPMENT_OPERATOR_MAP[equipType]
      if (opKey) {
        companions.push({
          ...EMPTY_ITEM,
          type: 'Labor', laborType: 'operator',
          description: opKey.label,
          qty: r.laborDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: operatorRate,
          crewSize: operatorCrewSize, totalHours: r.laborDays * operatorCrewSize * 8,
          _dragId: makeId()
        })
      }
    }

    // Set qty on the material item and queue companions for when the user saves
    setEditingItem(prev => ({ ...prev, qty: r.tons, unit: 'Ton' }))
    setMaterialCalcCompanions(companions)
    setShowMaterialCalc(false)
  }

  // ── Calculator helpers ─────────────────────────────────────
  const getCalcVal = (path) => {
    if (!calcConfig) return 0
    const parts = path.split('.')
    let val = calcConfig
    for (const p of parts) val = val?.[p]
    return val || 0
  }

  const calcGravel = () => {
    const coverage = getCalcVal(`gravelCoverage.${gravelInputs.type}`)
    const installRate = gravelInputs.installMethod === 'hand'
      ? getCalcVal('materialRates.gravelTonsPerDay')
      : gravelInputs.installMethod === 'tractor'
        ? getCalcVal('moveGravelRates.gravelTonsPerDayTractor')
        : getCalcVal('moveGravelRates.gravelTonsPerDayBobcat')
    const dayRate = getCalcVal('labor.dayRate')
    const equipRate = gravelInputs.installMethod === 'tractor'
      ? getCalcVal('equipment.tractorRentalDailyRate')
      : gravelInputs.installMethod === 'bobcat'
        ? getCalcVal('equipment.bobcatRentalDailyRate')
        : 0
    const diffFactor = getCalcVal(`difficultyFactors.${gravelInputs.difficulty}`)
    const depthMult = gravelInputs.depth / 2
    const tons = (gravelInputs.squareFeet / (coverage || 1)) * depthMult
    const adjRate = (installRate || 1) / (diffFactor || 1)
    const laborDays = tons / (adjRate || 1)
    const matCost = tons >= 10
      ? tons * getCalcVal('gravel.costPerTonOver9')
      : tons * getCalcVal('gravel.costPerTonUnder9')
    const laborCost = laborDays * dayRate
    const equipCost = laborDays * equipRate
    return { tons: +tons.toFixed(2), laborDays: +laborDays.toFixed(2), matCost: +matCost.toFixed(2), laborCost: +laborCost.toFixed(2), equipCost: +equipCost.toFixed(2), total: +(matCost + laborCost + equipCost).toFixed(2), dayRate, equipRate }
  }

  const calcPlants = () => {
    const dayRate = getCalcVal('labor.dayRate')
    let totalDays = 0
    plantRows.forEach(row => {
      if (row.quantity > 0) {
        const rate = getCalcVal(`plantRates.${row.size}`) || 1
        const diff = getCalcVal(`difficultyFactors.${row.difficulty}`) || 1
        const soil = getCalcVal(`soilFactors.${row.soilType}`) || 1
        totalDays += row.quantity / (rate / (diff * soil))
      }
    })
    return { laborDays: +totalDays.toFixed(2), laborCost: +(totalDays * dayRate).toFixed(2), dayRate }
  }

  const calcIrrigation = () => {
    const plantsPerValve = getCalcVal('irrigation.plantsPerValve') || 1
    const treesPerValve = getCalcVal('irrigation.treesPerValve') || 1
    const daysPerValve = getCalcVal('irrigation.daysPerValve')
    const costPerValve = getCalcVal('irrigation.costPerValve')
    const clockCost = getCalcVal('irrigation.clockCost')
    const backflowCost = getCalcVal('irrigation.backflowCost')
    const dayRate = getCalcVal('labor.dayRate')
    const pValves = Math.ceil(irrigationInputs.plantCount / plantsPerValve)
    const tValves = Math.ceil(irrigationInputs.treeCount / treesPerValve)
    const totalValves = pValves + tValves
    const matCost = (totalValves * costPerValve) + (irrigationInputs.includeClock ? clockCost : 0) + (irrigationInputs.includeBackflow ? backflowCost : 0)
    const laborDays = totalValves * daysPerValve
    return { totalValves, laborDays: +laborDays.toFixed(2), matCost: +matCost.toFixed(2), laborCost: +(laborDays * dayRate).toFixed(2), total: +(matCost + laborDays * dayRate).toFixed(2), dayRate }
  }

  const calcLighting = () => {
    const costPerLight = getCalcVal('lighting.costPerLight')
    const techRate = getCalcVal('lighting.techDailyRate')
    const lightsPerDay = getCalcVal('lighting.lightsInstalledPerDay') || 1
    const trenchHrs = getCalcVal('lighting.hoursTrenchingPerLight')
    const wiringCost = getCalcVal('lighting.wiringAndConnectorCostPerLight')
    const trenchRate = getCalcVal('lighting.hourlyTrenchingRate')
    const count = lightingInputs.lightCount
    const watts = count * lightingInputs.wattsPerLight
    let xfmrCost = 0
    if (watts <= 150) xfmrCost = getCalcVal('lighting.transformerCosts.w150')
    else if (watts <= 300) xfmrCost = getCalcVal('lighting.transformerCosts.w300')
    else if (watts <= 600) xfmrCost = getCalcVal('lighting.transformerCosts.w600')
    else if (watts <= 900) xfmrCost = getCalcVal('lighting.transformerCosts.w900')
    else xfmrCost = getCalcVal('lighting.transformerCosts.w1200')
    const matCost = (count * costPerLight) + (count * wiringCost) + xfmrCost
    const installDays = count / lightsPerDay
    const installCost = installDays * techRate
    const trenchCost = (count * trenchHrs) * trenchRate
    return { matCost: +matCost.toFixed(2), installDays: +installDays.toFixed(2), installCost: +installCost.toFixed(2), trenchCost: +trenchCost.toFixed(2), total: +(matCost + installCost + trenchCost).toFixed(2), techRate }
  }

  const makeId = () => `calc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

  const addGravelItems = () => {
    const r = calcGravel()
    const typeLabel = { quarterInch: '1/4"', halfInch: '1/2"', threeQuarterInch: '3/4"', oneInch: '1"', ripRap: 'Rip-Rap' }
    const items = []
    items.push({ ...EMPTY_ITEM, type: 'Materials', description: `${typeLabel[gravelInputs.type] || ''} Gravel`, qty: r.tons, unit: 'Ton', costPer: r.matCost / (r.tons || 1), pricePer: (r.matCost / (r.tons || 1)) * 1.4, _dragId: makeId() })
    items.push({ ...EMPTY_ITEM, type: 'Labor', laborType: 'labor', description: 'Install Labor', qty: r.laborDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: r.dayRate, crewSize: 1, totalHours: r.laborDays * 8, _dragId: makeId() })
    if (gravelInputs.installMethod !== 'hand') {
      const equipType = gravelInputs.installMethod === 'bobcat' ? 'bobcat' : 'tractor'
      items.push({ ...EMPTY_ITEM, type: 'Equipment', equipmentType: equipType, description: formatLabel(equipType), qty: r.laborDays, costPer: r.equipRate, pricePer: r.equipRate * 1.4, crewSize: 1, totalHours: r.laborDays * 8, _dragId: makeId() })
      // Auto-add operator
      const opKey = EQUIPMENT_OPERATOR_MAP[equipType]
      if (opKey) {
        const opRate = getRate('Labor', 'operator')
        items.push({ ...EMPTY_ITEM, type: 'Labor', laborType: 'operator', description: opKey.label, qty: r.laborDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: opRate, crewSize: 1, totalHours: r.laborDays * 8, _dragId: makeId() })
      }
    }
    setLineItems([...lineItems, ...items])
    setActiveCalc(null)
  }

  const addPlantLaborItems = () => {
    const r = calcPlants()
    if (r.laborDays > 0) {
      setLineItems([...lineItems, { ...EMPTY_ITEM, type: 'Labor', laborType: 'install', description: 'Plant Install Labor', qty: r.laborDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: r.dayRate, crewSize: 1, totalHours: r.laborDays * 8, _dragId: makeId() }])
    }
    setActiveCalc(null)
  }

  const addIrrigationItems = () => {
    const r = calcIrrigation()
    const items = []
    if (r.matCost > 0) items.push({ ...EMPTY_ITEM, type: 'Materials', description: 'Irrigation Supplies', qty: 1, unit: 'Each', costPer: r.matCost, pricePer: r.matCost * 1.4, _dragId: makeId() })
    if (r.laborDays > 0) items.push({ ...EMPTY_ITEM, type: 'Labor', laborType: 'irrigationTech', description: 'Irrigation Tech Labor', qty: r.laborDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: r.dayRate, crewSize: 1, totalHours: r.laborDays * 8, _dragId: makeId() })
    setLineItems([...lineItems, ...items])
    setActiveCalc(null)
  }

  const addLightingItems = () => {
    const r = calcLighting()
    const items = []
    if (r.matCost > 0) items.push({ ...EMPTY_ITEM, type: 'Materials', description: 'Lighting Materials & Transformer', qty: 1, unit: 'Each', costPer: r.matCost, pricePer: r.matCost * 1.4, _dragId: makeId() })
    if (r.installDays > 0) items.push({ ...EMPTY_ITEM, type: 'Labor', laborType: 'lightingTech', description: 'Lighting Install Labor', qty: r.installDays, costPer: DEFAULT_LABOR_BURDEN_RATE, pricePer: r.techRate, crewSize: 1, totalHours: r.installDays * 8, _dragId: makeId() })
    if (r.trenchCost > 0) items.push({ ...EMPTY_ITEM, type: 'Labor', laborType: 'labor', description: 'Trenching Labor', qty: 1, unit: 'Each', costPer: r.trenchCost, pricePer: r.trenchCost * 1.4, _dragId: makeId() })
    setLineItems([...lineItems, ...items])
    setActiveCalc(null)
  }

  // ── Render helpers ────────────────────────────────────────
  const autoDesc = buildAutoDescription(editingItem)
  const displayDesc = editingItem.description || autoDesc

  const renderDescriptionField = () => (
    <>
      <Grid item xs={12} md={8}>
        <TextField
          label="Description"
          value={editingItem.description}
          onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          placeholder={autoDesc || 'Enter description...'}
          helperText={!editingItem.description && autoDesc ? `Auto: "${autoDesc}"` : ''}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={classes.fieldSet}>
          <Typography variant="subtitle2" className={classes.fieldSetTitle}>
            Proposal Display
          </Typography>
          <TextField
            label="Display Override"
            value={editingItem.displayDescription || ''}
            onChange={(e) => setEditingItem({ ...editingItem, displayDescription: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Leave blank for auto"
            helperText="Custom text for Pretty Proposal"
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingItem.displayQty !== false}
                  onChange={(e) => setEditingItem({ ...editingItem, displayQty: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label={<span style={{ fontSize: 13 }}>Show Qty</span>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingItem.displaySize !== false}
                  onChange={(e) => setEditingItem({ ...editingItem, displaySize: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label={<span style={{ fontSize: 13 }}>Show Size</span>}
            />
          </div>
        </div>
      </Grid>
    </>
  )

  const renderMaterialsForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <div className={classes.fieldSet}>
          <Typography variant="subtitle2" className={classes.fieldSetTitle}>
            Material Selection
          </Typography>
          <Autocomplete
            options={takeOffList || []}
            getOptionLabel={(option) => option?.name || ''}
            value={selectedMaterial}
            onChange={handleMaterialChange}
            renderInput={(params) => (
              <TextField {...params} label="Select Material" fullWidth variant="outlined" />
            )}
          />
          {materialOptions.length > 0 && (
            <TextField
              select
              label="Size / Option"
              value={editingItem.optionId || ''}
              onChange={handleOptionChange}
              fullWidth
              variant="outlined"
              margin="normal"
            >
              {materialOptions.map((option) => (
                <MenuItem key={option._id} value={option._id}>
                  {option.name || option.size}
                  {option.msrp ? ` — $${option.msrp}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
        </div>
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={classes.fieldSet}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle} style={{ marginBottom: 0 }}>
              Quantity
            </Typography>
            {getMaterialCalcType() === 'tonnage' && (
              <Button
                size="small"
                variant={showMaterialCalc ? 'contained' : 'outlined'}
                color="primary"
                startIcon={<CalcIcon fontSize="small" />}
                onClick={() => {
                  setShowMaterialCalc(!showMaterialCalc)
                  if (!calcConfig && !showMaterialCalc) {
                    setCalcLoading(true)
                    Promise.all([
                      axios.get('/api/calculation-configs'),
                      axios.get('/api/calculation-configs/user/preference')
                    ]).then(([configsRes, prefRes]) => {
                      const allConfigs = configsRes.data.configs || []
                      const prefId = prefRes.data.configId
                      setCalcConfig((prefId ? allConfigs.find(c => c._id === prefId) : null) || allConfigs[0] || null)
                    }).catch(() => {}).finally(() => setCalcLoading(false))
                  }
                }}
                style={{ textTransform: 'none', fontSize: 11 }}
              >
                Calc
              </Button>
            )}
          </div>
          <TextField
            label="Qty"
            type="number"
            value={editingItem.qty}
            onChange={(e) => setEditingItem({ ...editingItem, qty: e.target.value })}
            fullWidth
            variant="outlined"
          />
          {!materialOptions.length && (
            <TextField
              select
              label="Unit"
              value={editingItem.unit || 'Each'}
              onChange={(e) => setEditingItem({ ...editingItem, unit: e.target.value })}
              fullWidth
              variant="outlined"
              margin="normal"
            >
              {unitsOfMeasure.map((u) => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </TextField>
          )}
        </div>
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={classes.fieldSet}>
          <Typography variant="subtitle2" className={classes.fieldSetTitle}>
            Pricing
          </Typography>
          <TextField
            label="Cost Per Unit"
            type="number"
            value={editingItem.costPer}
            onChange={(e) => setEditingItem({ ...editingItem, costPer: e.target.value })}
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Price Per Unit"
            type="number"
            value={editingItem.pricePer}
            onChange={(e) => setEditingItem({ ...editingItem, pricePer: e.target.value })}
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </div>
      </Grid>
      {/* Inline tonnage calculator */}
      {showMaterialCalc && getMaterialCalcType() === 'tonnage' && calcConfig && (() => {
        const r = calcTonnage()
        return (
          <Grid item xs={12}>
            <div style={{ border: '1px solid #1976d2', borderRadius: 6, padding: '12px 16px', background: '#f5f9ff' }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <TextField label="Sq Ft" type="number" value={materialCalcInputs.squareFeet}
                    onChange={e => setMaterialCalcInputs({...materialCalcInputs, squareFeet: parseInt(e.target.value) || 0})}
                    fullWidth variant="outlined" size="small" />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField label="Depth (in)" type="number" value={materialCalcInputs.depth}
                    onChange={e => setMaterialCalcInputs({...materialCalcInputs, depth: parseFloat(e.target.value) || 0})}
                    fullWidth variant="outlined" size="small" inputProps={{ step: 0.5 }} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField select label="Method" value={materialCalcInputs.installMethod}
                    onChange={e => setMaterialCalcInputs({...materialCalcInputs, installMethod: e.target.value})}
                    fullWidth variant="outlined" size="small">
                    <MenuItem value="hand">Hand</MenuItem>
                    <MenuItem value="tractor">Tractor</MenuItem>
                    <MenuItem value="bobcat">Bobcat</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField select label="Difficulty" value={materialCalcInputs.difficulty}
                    onChange={e => setMaterialCalcInputs({...materialCalcInputs, difficulty: e.target.value})}
                    fullWidth variant="outlined" size="small">
                    <MenuItem value="veryEasy">Easy</MenuItem>
                    <MenuItem value="standard">Std</MenuItem>
                    <MenuItem value="difficult">Hard</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: '#1976d2' }}>{r.tons}</span>
                    <span style={{ fontSize: 11, color: '#666', marginLeft: 4 }}>tons</span>
                    {r.laborDays > 0 && <div style={{ fontSize: 11, color: '#666' }}>{r.laborDays} days labor{materialCalcInputs.installMethod !== 'hand' ? ` + ${formatLabel(materialCalcInputs.installMethod)}` : ''}</div>}
                  </div>
                </Grid>
              </Grid>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, gap: 8 }}>
                <Button size="small" onClick={() => setShowMaterialCalc(false)} style={{ textTransform: 'none' }}>Cancel</Button>
                <Button
                  size="small" variant="contained" color="primary"
                  disabled={r.tons <= 0}
                  onClick={handleApplyTonnageCalc}
                  style={{ textTransform: 'none' }}
                >
                  Apply {r.tons} tons{r.laborDays > 0 ? ' + add labor' : ''}
                </Button>
              </div>
            </div>
          </Grid>
        )
      })()}
      {renderDescriptionField()}
    </Grid>
  )

  const renderLaborEquipmentForm = () => {
    const rateTypes = editingItem.type === 'Labor' ? laborRates : equipmentRates

    // For equipment, only show actual equipment (not operators or _id)
    const filteredRateEntries = rateTypes
      ? Object.entries(rateTypes).filter(([key]) => {
          if (key === '_id') return false
          if (editingItem.type === 'Equipment') return EQUIPMENT_ONLY_KEYS.has(key)
          return true
        })
      : []

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <div className={classes.fieldSet}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle}>
              {editingItem.type === 'Equipment' ? 'Equipment' : editingItem.type} Type
            </Typography>
            <TextField
              select
              label={editingItem.type === 'Equipment' ? 'Select Equipment' : `${editingItem.type} Type`}
              value={editingItem.type === 'Labor' ? editingItem.laborType : editingItem.equipmentType}
              onChange={handleRateTypeChange}
              fullWidth
              variant="outlined"
              helperText={editingItem.type === 'Equipment' ? 'Operator labor will be added automatically' : ''}
            >
              {filteredRateEntries.map(([key, rate]) => (
                <MenuItem key={key} value={key}>
                  {formatLabel(key)} {rate?.rate ? `— $${rate.rate}${editingItem.type === 'Equipment' ? '/day' : '/hr'}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Number of Days"
              type="number"
              inputProps={{ step: '0.25' }}
              value={editingItem.qty}
              onChange={handleQuantityChange}
              fullWidth
              variant="outlined"
              margin="normal"
            />
          </div>
        </Grid>
        <Grid item xs={12} md={4}>
          <div className={classes.fieldSet}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle}>
              Rates
            </Typography>
            <TextField
              label={editingItem.type === 'Equipment' ? 'Cost Per Day' : 'Cost Per Hour'}
              type="number"
              value={editingItem.costPer}
              onChange={(e) => setEditingItem({ ...editingItem, costPer: e.target.value })}
              fullWidth
              variant="outlined"
            />
            <TextField
              label={editingItem.type === 'Equipment' ? 'Price Per Day' : 'Price Per Hour'}
              type="number"
              value={editingItem.pricePer}
              onChange={(e) => setEditingItem({ ...editingItem, pricePer: e.target.value })}
              fullWidth
              variant="outlined"
              margin="normal"
            />
          </div>
        </Grid>
        <Grid item xs={12} md={4}>
          <div className={classes.fieldSet}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle}>
              Crew Info
            </Typography>
            <TextField
              label="Crew Size"
              type="number"
              value={editingItem.crewSize || 1}
              onChange={(e) => {
                const cs = parseFloat(e.target.value) || 1
                setEditingItem(prev => ({
                  ...prev,
                  crewSize: cs,
                  totalHours: cs * (parseFloat(prev.qty) || 0) * 8
                }))
              }}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Total Hours"
              type="number"
              value={editingItem.totalHours || 0}
              disabled
              fullWidth
              variant="outlined"
              margin="normal"
            />
          </div>
        </Grid>
        {renderDescriptionField()}
      </Grid>
    )
  }

  const renderSubContractorForm = () => {
    const subCost = parseFloat(editingItem.qty) || 0
    const markupPct = parseFloat(editingItem._markupPct != null ? editingItem._markupPct : 15)
    const multiplier = 1 + (markupPct / 100)
    const markupAmt = subCost * (markupPct / 100)
    const clientPrice = subCost * multiplier

    const handleSubCostChange = (e) => {
      const cost = e.target.value
      setEditingItem(prev => ({
        ...prev,
        qty: cost,
        costPer: 1,
        pricePer: +(1 + ((parseFloat(prev._markupPct != null ? prev._markupPct : 15)) / 100)).toFixed(4)
      }))
    }

    const handleMarkupChange = (e) => {
      const pct = e.target.value
      setEditingItem(prev => ({
        ...prev,
        _markupPct: pct,
        pricePer: +(1 + (parseFloat(pct || 0) / 100)).toFixed(4)
      }))
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <div className={classes.fieldSet}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle}>
              Sub-Contractor
            </Typography>
            <TextField
              label="Vendor / Company (internal only)"
              value={editingItem.description}
              onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g. ABC Plumbing"
              helperText="Not shown on proposal"
            />
            <TextField
              label="Scope of Work (shown on proposal)"
              value={editingItem.displayDescription || ''}
              onChange={(e) => setEditingItem({ ...editingItem, displayDescription: e.target.value })}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              margin="normal"
              size="small"
              placeholder="What are they doing?"
              helperText="This is what the client sees"
            />
          </div>
        </Grid>
        <Grid item xs={12} md={4}>
          <div className={classes.fieldSet}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle}>
              Pricing
            </Typography>
            <TextField
              label="Sub-Contractor Cost"
              type="number"
              value={editingItem.qty}
              onChange={handleSubCostChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ startAdornment: <span style={{ color: '#999', marginRight: 2 }}>$</span> }}
              helperText="The dollar amount you pay the sub"
            />
            <TextField
              label="Markup %"
              type="number"
              value={editingItem._markupPct != null ? editingItem._markupPct : 15}
              onChange={handleMarkupChange}
              fullWidth
              variant="outlined"
              margin="normal"
              size="small"
              inputProps={{ step: '1', min: '0' }}
              InputProps={{ endAdornment: <span style={{ color: '#999', marginLeft: 2 }}>%</span> }}
              helperText={subCost > 0 ? `+${currencyFormat(markupAmt)} markup` : 'Default 15%'}
            />
          </div>
        </Grid>
        <Grid item xs={12} md={3}>
          <div className={classes.fieldSet}>
            <Typography variant="subtitle2" className={classes.fieldSetTitle}>
              Summary
            </Typography>
            <div style={{ padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>Sub Cost:</span>
                <span style={{ fontWeight: 600 }}>{currencyFormat(subCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>Markup ({markupPct}%):</span>
                <span style={{ fontWeight: 600 }}>+{currencyFormat(markupAmt)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #1976d2', paddingTop: 8 }}>
                <span style={{ color: '#1976d2', fontSize: 13, fontWeight: 600 }}>Client Price:</span>
                <span style={{ fontWeight: 700, color: '#1976d2', fontSize: 16 }}>{currencyFormat(clientPrice)}</span>
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
    )
  }

  const renderNotesMeasurementsForm = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          label="Details"
          value={editingItem.description}
          onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
        />
      </Grid>
    </Grid>
  )

  const formatRowQuantity = (item) => {
    if (item.type === 'Labor' || item.type === 'Equipment') {
      return `${item.qty} day${item.qty != 1 ? 's' : ''} × ${item.crewSize || 1} crew`
    }
    return `${item.qty || ''} ${item.unit || ''}`
  }

  const formatRowPrice = (item, field) => {
    if (item.type === 'Notes' || item.type === 'Measurements') return ''
    const val = parseFloat(item[field] || 0)
    const suffix = item.type === 'Equipment' ? '/day' : item.type === 'Labor' ? '/hr' : ''
    return `$${val.toFixed(2)}${suffix}`
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>Line Item Builder</DialogTitle>
      <DialogContent>
        {/* ── Line Items Table ──────────────────────────────── */}
        <Paper className={classes.tableContainer}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.dragHandleCell} />
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Tax</TableCell>
                  <TableCell className={classes.actionsCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="lineItemList">
                  {(droppableProvided) => (
                    <TableBody
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {lineItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center" style={{ padding: 24, color: '#999' }}>
                            No items yet — use the form below to add line items
                          </TableCell>
                        </TableRow>
                      )}
                      {lineItems.map((item, index) => (
                        <Draggable
                          key={getDragId(item, index)}
                          draggableId={getDragId(item, index)}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`${classes.tableRow} ${selectedIndex === index ? classes.selectedRow : ''}`}
                              onClick={() => handleEditItem(index)}
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging ? { background: '#e3f2fd', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } : {})
                              }}
                            >
                              <TableCell
                                className={classes.dragHandleCell}
                                {...provided.dragHandleProps}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DragIndicatorIcon fontSize="small" />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.type}
                                  size="small"
                                  className={classes.typeChip}
                                  style={{ backgroundColor: TYPE_COLORS[item.type] || '#999', color: '#fff' }}
                                />
                              </TableCell>
                              <TableCell>{item.description || '—'}</TableCell>
                              <TableCell align="right">{formatRowQuantity(item)}</TableCell>
                              <TableCell align="right">{formatRowPrice(item, 'costPer')}</TableCell>
                              <TableCell align="right">{formatRowPrice(item, 'pricePer')}</TableCell>
                              <TableCell align="right">
                                {(item.type !== 'Notes' && item.type !== 'Measurements')
                                  ? currencyFormat(calculateTotal(item))
                                  : ''}
                              </TableCell>
                              <TableCell align="center">
                                {(item.type !== 'Notes' && item.type !== 'Measurements') && (
                                  <Checkbox checked={item.taxable} disabled size="small" />
                                )}
                              </TableCell>
                              <TableCell className={classes.actionsCell}>
                                <Tooltip title="Duplicate">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleDuplicate(index) }}
                                  >
                                    <DuplicateIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(index) }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </TableContainer>
        </Paper>

        {/* ── Calculator Toolbar ───────────────────────────── */}
        <div className={classes.calcToolbar}>
          <Typography variant="body2" style={{ lineHeight: '36px', marginRight: 8, color: '#666' }}>Calculators:</Typography>
          {['plants', 'gravel', 'irrigation', 'lighting'].map(calc => (
            <Button
              key={calc}
              size="small"
              variant={activeCalc === calc ? 'contained' : 'outlined'}
              color="primary"
              className={classes.calcButton}
              startIcon={<CalcIcon fontSize="small" />}
              onClick={() => setActiveCalc(activeCalc === calc ? null : calc)}
            >
              {calc.charAt(0).toUpperCase() + calc.slice(1)}
            </Button>
          ))}
        </div>

        {/* ── Calculator Panels ─────────────────────────────── */}
        {activeCalc && calcConfig && (
          <Paper className={classes.calcPanel}>
            {activeCalc === 'gravel' && (() => {
              const r = calcGravel()
              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}><Typography variant="subtitle1" style={{ fontWeight: 600 }}>Gravel Calculator</Typography></Grid>
                  <Grid item xs={6} md={3}>
                    <TextField select label="Type" value={gravelInputs.type} onChange={e => setGravelInputs({...gravelInputs, type: e.target.value})} fullWidth variant="outlined" size="small">
                      <MenuItem value="quarterInch">1/4"</MenuItem>
                      <MenuItem value="halfInch">1/2"</MenuItem>
                      <MenuItem value="threeQuarterInch">3/4"</MenuItem>
                      <MenuItem value="oneInch">1"</MenuItem>
                      <MenuItem value="ripRap">Rip-Rap</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField label="Sq Ft" type="number" value={gravelInputs.squareFeet} onChange={e => setGravelInputs({...gravelInputs, squareFeet: parseInt(e.target.value) || 0})} fullWidth variant="outlined" size="small" />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField label="Depth (in)" type="number" value={gravelInputs.depth} onChange={e => setGravelInputs({...gravelInputs, depth: parseFloat(e.target.value) || 0})} fullWidth variant="outlined" size="small" inputProps={{ step: 0.5 }} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField select label="Install Method" value={gravelInputs.installMethod} onChange={e => setGravelInputs({...gravelInputs, installMethod: e.target.value})} fullWidth variant="outlined" size="small">
                      <MenuItem value="hand">By Hand</MenuItem>
                      <MenuItem value="tractor">Tractor</MenuItem>
                      <MenuItem value="bobcat">Bobcat</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField select label="Difficulty" value={gravelInputs.difficulty} onChange={e => setGravelInputs({...gravelInputs, difficulty: e.target.value})} fullWidth variant="outlined" size="small">
                      <MenuItem value="veryEasy">Easy</MenuItem>
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="difficult">Hard</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell>Tons Needed</TableCell><TableCell align="right"><strong>{r.tons}</strong></TableCell></TableRow>
                        <TableRow><TableCell>Labor Days</TableCell><TableCell align="right">{r.laborDays}</TableCell></TableRow>
                        <TableRow><TableCell>Material Cost</TableCell><TableCell align="right">${r.matCost.toFixed(2)}</TableCell></TableRow>
                        <TableRow><TableCell>Labor Cost</TableCell><TableCell align="right">${r.laborCost.toFixed(2)}</TableCell></TableRow>
                        {r.equipCost > 0 && <TableRow><TableCell>Equipment Cost</TableCell><TableCell align="right">${r.equipCost.toFixed(2)}</TableCell></TableRow>}
                        <TableRow className={classes.calcResultRow}><TableCell><strong>Total</strong></TableCell><TableCell align="right"><strong>${r.total.toFixed(2)}</strong></TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="contained" color="primary" fullWidth onClick={addGravelItems}>Add Gravel + Labor to List</Button>
                  </Grid>
                </Grid>
              )
            })()}

            {activeCalc === 'plants' && (() => {
              const r = calcPlants()
              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Plant Labor Calculator</Typography>
                      <Button size="small" variant="outlined" onClick={() => setPlantRows([...plantRows, { size: 'mediumPlants', quantity: 0, difficulty: 'standard', soilType: 'loamy' }])}>+ Add Row</Button>
                    </div>
                  </Grid>
                  {plantRows.map((row, idx) => (
                    <React.Fragment key={idx}>
                      <Grid item xs={3}>
                        <TextField select label="Size" value={row.size} onChange={e => { const nr = [...plantRows]; nr[idx].size = e.target.value; setPlantRows(nr) }} fullWidth variant="outlined" size="small">
                          <MenuItem value="flatFlowers">Flat</MenuItem>
                          <MenuItem value="pintFlowers">Pint</MenuItem>
                          <MenuItem value="quartFlowers">Quart</MenuItem>
                          <MenuItem value="smallPlants">#1</MenuItem>
                          <MenuItem value="mediumPlants">#5</MenuItem>
                          <MenuItem value="largePlants">#15</MenuItem>
                          <MenuItem value="mediumTrees">24" Box</MenuItem>
                          <MenuItem value="largeTrees">36"+ Box</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={2}>
                        <TextField label="Qty" type="number" value={row.quantity} onChange={e => { const nr = [...plantRows]; nr[idx].quantity = parseInt(e.target.value) || 0; setPlantRows(nr) }} fullWidth variant="outlined" size="small" />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField select label="Difficulty" value={row.difficulty} onChange={e => { const nr = [...plantRows]; nr[idx].difficulty = e.target.value; setPlantRows(nr) }} fullWidth variant="outlined" size="small">
                          <MenuItem value="veryEasy">Easy</MenuItem>
                          <MenuItem value="standard">Standard</MenuItem>
                          <MenuItem value="difficult">Hard</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField select label="Soil" value={row.soilType} onChange={e => { const nr = [...plantRows]; nr[idx].soilType = e.target.value; setPlantRows(nr) }} fullWidth variant="outlined" size="small">
                          <MenuItem value="sandy">Sandy</MenuItem>
                          <MenuItem value="loamy">Loamy</MenuItem>
                          <MenuItem value="clay">Clay</MenuItem>
                          <MenuItem value="rocky">Rocky</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={1}>{plantRows.length > 1 && <Button size="small" color="secondary" onClick={() => setPlantRows(plantRows.filter((_, i) => i !== idx))}>×</Button>}</Grid>
                    </React.Fragment>
                  ))}
                  <Grid item xs={12} md={6}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell>Labor Days</TableCell><TableCell align="right">{r.laborDays}</TableCell></TableRow>
                        <TableRow><TableCell>Man Hours</TableCell><TableCell align="right">{(r.laborDays * 8).toFixed(1)} hrs</TableCell></TableRow>
                        <TableRow className={classes.calcResultRow}><TableCell><strong>Labor Cost</strong></TableCell><TableCell align="right"><strong>${r.laborCost.toFixed(2)}</strong></TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="contained" color="primary" fullWidth onClick={addPlantLaborItems} disabled={r.laborDays <= 0}>Add Install Labor to List</Button>
                  </Grid>
                </Grid>
              )
            })()}

            {activeCalc === 'irrigation' && (() => {
              const r = calcIrrigation()
              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}><Typography variant="subtitle1" style={{ fontWeight: 600 }}>Irrigation Calculator</Typography></Grid>
                  <Grid item xs={6} md={3}>
                    <TextField label="Plant Count" type="number" value={irrigationInputs.plantCount} onChange={e => setIrrigationInputs({...irrigationInputs, plantCount: parseInt(e.target.value) || 0})} fullWidth variant="outlined" size="small" helperText="Auto-counted from items" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField label="Tree Count" type="number" value={irrigationInputs.treeCount} onChange={e => setIrrigationInputs({...irrigationInputs, treeCount: parseInt(e.target.value) || 0})} fullWidth variant="outlined" size="small" helperText="Auto-counted from items" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Button variant={irrigationInputs.includeBackflow ? 'contained' : 'outlined'} color="primary" size="small" fullWidth onClick={() => setIrrigationInputs({...irrigationInputs, includeBackflow: !irrigationInputs.includeBackflow})}>Backflow</Button>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Button variant={irrigationInputs.includeClock ? 'contained' : 'outlined'} color="primary" size="small" fullWidth onClick={() => setIrrigationInputs({...irrigationInputs, includeClock: !irrigationInputs.includeClock})}>Clock</Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell>Valves Needed</TableCell><TableCell align="right">{r.totalValves}</TableCell></TableRow>
                        <TableRow><TableCell>Labor Days</TableCell><TableCell align="right">{r.laborDays}</TableCell></TableRow>
                        <TableRow><TableCell>Material Cost</TableCell><TableCell align="right">${r.matCost.toFixed(2)}</TableCell></TableRow>
                        <TableRow><TableCell>Labor Cost</TableCell><TableCell align="right">${r.laborCost.toFixed(2)}</TableCell></TableRow>
                        <TableRow className={classes.calcResultRow}><TableCell><strong>Total</strong></TableCell><TableCell align="right"><strong>${r.total.toFixed(2)}</strong></TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="contained" color="primary" fullWidth onClick={addIrrigationItems}>Add Irrigation Items to List</Button>
                  </Grid>
                </Grid>
              )
            })()}

            {activeCalc === 'lighting' && (() => {
              const r = calcLighting()
              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}><Typography variant="subtitle1" style={{ fontWeight: 600 }}>Lighting Calculator</Typography></Grid>
                  <Grid item xs={6} md={3}>
                    <TextField label="Light Count" type="number" value={lightingInputs.lightCount} onChange={e => setLightingInputs({...lightingInputs, lightCount: parseInt(e.target.value) || 0})} fullWidth variant="outlined" size="small" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField label="Watts/Light" type="number" value={lightingInputs.wattsPerLight} onChange={e => setLightingInputs({...lightingInputs, wattsPerLight: parseInt(e.target.value) || 0})} fullWidth variant="outlined" size="small" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Table size="small">
                      <TableBody>
                        <TableRow><TableCell>Material Cost</TableCell><TableCell align="right">${r.matCost.toFixed(2)}</TableCell></TableRow>
                        <TableRow><TableCell>Install ({r.installDays} days)</TableCell><TableCell align="right">${r.installCost.toFixed(2)}</TableCell></TableRow>
                        <TableRow><TableCell>Trenching</TableCell><TableCell align="right">${r.trenchCost.toFixed(2)}</TableCell></TableRow>
                        <TableRow className={classes.calcResultRow}><TableCell><strong>Total</strong></TableCell><TableCell align="right"><strong>${r.total.toFixed(2)}</strong></TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button variant="contained" color="primary" fullWidth onClick={addLightingItems}>Add Lighting Items to List</Button>
                  </Grid>
                </Grid>
              )
            })()}

            {calcLoading && <Typography style={{ textAlign: 'center', padding: 24, color: '#999' }}>Loading calculator config...</Typography>}
            {!calcLoading && !calcConfig && <Typography style={{ textAlign: 'center', padding: 24, color: '#c62828' }}>No calculation config found. Set one up in Settings.</Typography>}
          </Paper>
        )}

        {/* ── Edit / Add Form ───────────────────────────────── */}
        <Paper className={classes.formSection}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" className={classes.formTitle}>
                {editMode === 'new' ? 'Add New Item' : `Editing Item #${selectedIndex + 1}`}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Type"
                value={editingItem.type}
                onChange={handleTypeChange}
                fullWidth
                variant="outlined"
                size="small"
              >
                {lineItemTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {editingItem.type === 'Materials' && renderMaterialsForm()}
          {(editingItem.type === 'Labor' || editingItem.type === 'Equipment') && renderLaborEquipmentForm()}
          {editingItem.type === 'SubContractor' && renderSubContractorForm()}
          {(editingItem.type === 'Notes' || editingItem.type === 'Measurements') && renderNotesMeasurementsForm()}

          {editingItem.type && editingItem.type !== 'Notes' && editingItem.type !== 'Measurements' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingItem.taxable}
                  onChange={(e) => setEditingItem({ ...editingItem, taxable: e.target.checked })}
                  color="primary"
                />
              }
              label="Taxable"
              style={{ marginTop: 8 }}
            />
          )}

          {(editingItem?.pricePer && editingItem?.qty) ? (
            <Paper className={classes.total}>
              <Typography variant="h6" className={classes.totalText}>
                Line Item Total: {currencyFormat(calculateTotal(editingItem))}
              </Typography>
            </Paper>
          ) : null}

          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!isItemValid(editingItem)}
            >
              {editMode === 'new' ? 'Add Item' : 'Update Item'}
            </Button>
            {editMode === 'edit' && (
              <Button variant="outlined" onClick={handleCancelEdit}>
                Cancel
              </Button>
            )}
          </div>
        </Paper>

        {/* ── Grand Total ───────────────────────────────────── */}
        <Paper className={classes.total}>
          <Typography variant="h6" className={classes.totalText}>
            Grand Total: {currencyFormat(calculateGrandTotal())}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSaveLineItems}
          color="primary"
          variant="contained"
          disabled={lineItems.length === 0}
        >
          Save All Items
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const mapStateToProps = (state) => {
  return {
    url: state.url,
    user: state.user,
    notification: state.notification
  }
}

export default connect(mapStateToProps)(LineItemBuilder)
